// Page Offre Fondateur - Design SaaS Premium Émotionnel
// Inspiré de Stripe, Shopify, Airbnb, Uber
// Mobile-first, sections modernes, gros boutons CTA

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, Rocket, CheckCircle2, Zap, Users, Store, MessageSquare,
  FileText, BarChart3, Calendar, Wrench, Globe, Bot, Star, ArrowRight,
  Shield, Clock, TrendingUp, Smartphone, ChevronRight, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export default function OffreFondateurPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="offre-fondateur-page">
      {/* ══════════════════════════════════════════════════════════════════
          HERO ÉMOTIONNEL
      ══════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        {/* Background effects */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500 rounded-full filter blur-[120px]"></div>
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-blue-500 rounded-full filter blur-[100px]"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 py-16 sm:py-24">
          {/* Badge */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex justify-center mb-8"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium">
              <Rocket size={16} />
              Programme Fondateur 2025
            </span>
          </motion.div>
          
          {/* Titre principal */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center leading-tight mb-6"
          >
            Artisans, il est temps que la technologie
            <span className="block text-emerald-400 mt-2">travaille enfin pour toi.</span>
          </motion.h1>
          
          {/* Sous-titre émotionnel */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-lg sm:text-xl text-slate-300 mb-6">
              Aujourd'hui, beaucoup d'artisans sont débordés :
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {['Devis', 'Factures', 'Organisation', 'Prospection', 'Réseaux sociaux', 'Administratif'].map((item, i) => (
                <span key={i} className="px-3 py-1.5 bg-white/10 rounded-full text-sm text-slate-200">
                  {item}
                </span>
              ))}
            </div>
            <p className="text-slate-400 mb-4">
              Et les outils digitaux sont souvent trop compliqués.
            </p>
            <p className="text-slate-300 font-medium">
              À partir de 2026, les factures électroniques deviendront <span className="text-white">obligatoires</span>.
            </p>
            <p className="text-emerald-400 font-semibold mt-2">
              La digitalisation n'est plus une option.
            </p>
          </motion.div>
          
          {/* CTA Hero */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10"
          >
            <Link to="/batiassist">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg shadow-emerald-500/30">
                <Rocket size={20} className="mr-2" />
                Créer ma boutique fondateur
              </Button>
            </Link>
            <a href="#offre" className="text-slate-400 hover:text-white transition-colors flex items-center gap-1">
              Voir l'offre <ChevronRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          MESSAGE CENTRAL
      ══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 mb-6">
              <Building2 size={32} className="text-[#F97316]" />
              <span className="text-2xl font-bold text-slate-900">AbracadaBati</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              Le premier centre commercial du bâtiment sur internet.
            </h2>
            <p className="text-lg text-slate-600 mb-6">
              Notre objectif : <span className="font-semibold text-slate-900">donner aux artisans une entreprise digitale complète prête à l'emploi.</span>
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-slate-500">
              <span className="flex items-center gap-2">
                <span className="w-6 h-[2px] bg-red-400"></span>
                Pas un logiciel compliqué
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 size={20} className="text-emerald-500" />
                Une solution qui travaille pour toi
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION ÉMOTION
      ══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              Votre métier est sur les chantiers.
              <span className="block text-[#1E40AF]">Pas derrière un ordinateur.</span>
            </h2>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MessageSquare, text: 'Répondre aux messages', color: 'bg-blue-100 text-blue-600' },
              { icon: FileText, text: 'Faire des devis', color: 'bg-orange-100 text-orange-600' },
              { icon: Users, text: 'Gérer les clients', color: 'bg-purple-100 text-purple-600' },
              { icon: Globe, text: 'Publier sur les réseaux', color: 'bg-pink-100 text-pink-600' },
              { icon: TrendingUp, text: 'Chercher de nouveaux chantiers', color: 'bg-green-100 text-green-600' },
              { icon: Clock, text: 'Des heures chaque semaine', color: 'bg-red-100 text-red-600' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm"
              >
                <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4`}>
                  <item.icon size={24} />
                </div>
                <p className="font-medium text-slate-900">{item.text}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center text-xl font-semibold text-emerald-600 mt-12"
          >
            Avec AbracadaBati, une grande partie de ce travail peut être automatisée.
          </motion.p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION SOLUTION - CRM
      ══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-6">
              <Sparkles size={16} />
              Ce que tu reçois
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Une entreprise digitale <span className="text-emerald-400">prête à l'emploi</span>
            </h2>
          </motion.div>
          
          {/* CRM Section */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-slate-800/50 backdrop-blur rounded-3xl p-8 sm:p-12 mb-12 border border-slate-700"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <BarChart3 size={24} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">CRM Bâtiment prêt à l'emploi</h3>
                </div>
                <p className="text-slate-300 mb-6">
                  Un CRM conçu par un entrepreneur du bâtiment. Il contient tout ce dont tu as besoin :
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {['Devis', 'Factures', 'Pipeline commercial', 'Pipeline chantier', 'Suivi clients', 'Gestion projets'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-300">
                      <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <p className="text-emerald-400 font-medium">
                    Les devis sont déjà pré-remplis par métier. Les descriptifs sont déjà écrits. Tu n'as qu'à ajouter tes prix.
                  </p>
                </div>
              </div>
              <div className="bg-slate-700/50 rounded-2xl p-4 border border-slate-600">
                {/* Mock CRM Screenshot - Pipeline */}
                <div className="bg-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-slate-400">Pipeline Commercial</span>
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">Live</span>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {[
                      { name: 'Prospects', count: 12, color: 'bg-blue-500' },
                      { name: 'Devis envoyés', count: 8, color: 'bg-yellow-500' },
                      { name: 'Négociation', count: 3, color: 'bg-orange-500' },
                      { name: 'Gagnés', count: 5, color: 'bg-emerald-500' },
                    ].map((col, i) => (
                      <div key={i} className="flex-shrink-0 w-32 bg-slate-700/50 rounded-lg p-3">
                        <div className={`w-2 h-2 ${col.color} rounded-full mb-2`}></div>
                        <p className="text-xs text-slate-400">{col.name}</p>
                        <p className="text-lg font-bold text-white">{col.count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Boutique Section */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-slate-800/50 backdrop-blur rounded-3xl p-8 sm:p-12 mb-12 border border-slate-700"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1 bg-slate-700/50 rounded-2xl p-4 border border-slate-600">
                {/* Mock Boutique Screenshot */}
                <div className="bg-white rounded-xl p-4 text-slate-900">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#F97316] rounded-lg flex items-center justify-center">
                      <Wrench size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">Plomberie Martin</p>
                      <p className="text-xs text-slate-500">Paris 15e • 4.9 ★</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {['Dépannage', 'Installation', 'Rénovation SdB', 'Devis gratuit'].map((s, i) => (
                      <div key={i} className="bg-slate-100 rounded-lg p-2 text-xs text-center">{s}</div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#F97316] rounded-xl flex items-center justify-center">
                    <Store size={24} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">Boutique + Site internet</h3>
                </div>
                <p className="text-slate-300 mb-6">
                  Chaque artisan obtient :
                </p>
                <ul className="space-y-3">
                  {[
                    'Une boutique dans AbracadaBati',
                    'Un site internet professionnel',
                    'Une visibilité dans le moteur de recherche',
                    'Connexion possible de ton nom de domaine'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-emerald-400 font-medium">
                  Votre entreprise devient visible en ligne immédiatement.
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Agents IA Section */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-gradient-to-br from-purple-900/50 to-slate-800/50 backdrop-blur rounded-3xl p-8 sm:p-12 mb-12 border border-purple-500/20"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Bot size={24} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">Agents IA intégrés</h3>
                </div>
                <p className="text-slate-300 mb-6">
                  Vos agents IA peuvent :
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    'Répondre aux messages',
                    'Assister la communication',
                    'Présenter tes services',
                    'Gérer les tâches répétitives',
                    'Répondre aux avis Google',
                    'Aider sur les réseaux sociaux'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-300">
                      <Zap size={14} className="text-purple-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <p className="text-sm text-slate-400">
                    <span className="text-yellow-400">⚠️</span> Le standard téléphonique IA reste une option.
                  </p>
                </div>
              </div>
              <div className="bg-slate-800/80 rounded-2xl p-4 border border-purple-500/20">
                {/* Mock Agent IA Screenshot */}
                <div className="bg-slate-900 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <Bot size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Agent IA - Messagerie</p>
                      <p className="text-xs text-emerald-400">En ligne</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-slate-700 rounded-lg p-3 text-sm text-slate-300 max-w-[80%]">
                      Bonjour, je souhaite un devis pour ma salle de bain.
                    </div>
                    <div className="bg-purple-500/20 rounded-lg p-3 text-sm text-purple-200 max-w-[80%] ml-auto">
                      Bonjour ! Je peux t'aider. Peux-tu me préciser la surface et le type de travaux ?
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Réseau Social + Moteur de recherche */}
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-slate-800/50 backdrop-blur rounded-3xl p-8 border border-slate-700"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center">
                  <Users size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold">Réseau social du bâtiment</h3>
              </div>
              <p className="text-slate-300 mb-4">
                Le premier réseau social du bâtiment. Les artisans peuvent :
              </p>
              <ul className="space-y-2">
                {['Montrer leurs chantiers', 'Partager leurs réalisations', 'Publier des vidéos', 'Développer leur réputation'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle2 size={14} className="text-pink-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-slate-800/50 backdrop-blur rounded-3xl p-8 border border-slate-700"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                  <TrendingUp size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold">Moteur de recherche clients</h3>
              </div>
              <p className="text-slate-300 mb-4">
                Quand un particulier cherche un artisan, décrit un projet ou demande un devis :
              </p>
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
                <p className="text-cyan-400 font-medium text-sm">
                  L'algorithme propose les artisans les plus proches et les mieux adaptés.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION MISE EN SERVICE
      ══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-emerald-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
              <Shield size={16} />
              Installation clé en main
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Nous nous occupons de tout pour toi
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Pour éviter toute complexité technique, nous installons ton entreprise digitale.
            </p>
            
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-emerald-100 mb-8">
              <div className="text-5xl font-bold text-emerald-600 mb-2">149 €</div>
              <p className="text-slate-500 mb-6">Prix unique de mise en service</p>
              
              <div className="grid sm:grid-cols-2 gap-4 text-left max-w-lg mx-auto">
                {[
                  'Création de la boutique',
                  'Configuration du CRM',
                  'Paramétrage des prestations',
                  'Connexion réseaux sociaux',
                  'Entraînement des agents IA',
                  'Prêt en 7 jours'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <p className="text-emerald-700 font-semibold text-lg">
              Votre entreprise digitale est prête en 7 jours. Vous n'avez rien à configurer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION OFFRE FONDATEUR
      ══════════════════════════════════════════════════════════════════ */}
      <section id="offre" className="py-16 sm:py-24 px-4 bg-gradient-to-br from-[#1E40AF] to-[#1E3A8A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white text-sm font-medium mb-6">
              <Star size={16} className="text-yellow-400" />
              Offre limitée
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-8">
              Programme Fondateur
            </h2>
            
            <div className="bg-white/10 backdrop-blur rounded-3xl p-8 sm:p-12 border border-white/20 mb-8">
              <div className="mb-8">
                <p className="text-slate-300 mb-2">Prix officiel de la plateforme</p>
                <p className="text-2xl text-slate-400 line-through">99 € / mois</p>
              </div>
              
              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 mb-8">
                <p className="text-emerald-400 font-semibold mb-2">Offre de lancement</p>
                <p className="text-4xl sm:text-5xl font-bold text-white">12 mois offerts</p>
              </div>
              
              <p className="text-lg text-slate-300 mb-2">Vous payez uniquement :</p>
              <p className="text-3xl font-bold text-white mb-2">149 € de mise en service</p>
              <p className="text-slate-400">pour l'installation complète</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/batiassist">
                <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg">
                  <Rocket size={20} className="mr-2" />
                  Créer ma boutique fondateur
                </Button>
              </Link>
              <Link to="/reseau/inscription">
                <Button size="lg" variant="outline" className="border-2 border-white/50 text-white hover:bg-white hover:text-[#1E40AF] px-8 py-6 text-lg font-semibold rounded-xl">
                  Rejoindre le programme fondateur
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION MISSION
      ══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Notre mission
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Le bâtiment est un métier difficile. Les artisans sont souvent seuls face à l'administratif, au digital, à la prospection.
            </p>
            <p className="text-xl font-semibold text-[#1E40AF] mb-8">
              AbracadaBati a été créé pour changer cela.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Wrench, title: 'Des outils simples', desc: 'Conçus pour les artisans' },
                { icon: Globe, title: 'De la visibilité', desc: 'Pour trouver des clients' },
                { icon: BarChart3, title: 'Une organisation moderne', desc: 'Dans une seule plateforme' }
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-2xl p-6">
                  <div className="w-12 h-12 bg-[#1E40AF]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon size={24} className="text-[#1E40AF]" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          FOOTER CTA
      ══════════════════════════════════════════════════════════════════ */}
      <section className="py-12 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Prêt à rejoindre les fondateurs ?</h3>
          <p className="text-slate-400 mb-6">12 mois offerts • Installation clé en main • Support dédié</p>
          <Link to="/batiassist">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg font-semibold rounded-xl">
              Commencer maintenant <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
